import { signInWithPopup } from "firebase/auth";
import { ChevronDown, ChevronRight, Mail, TerminalSquare } from "lucide-react";
import { useCallback, useState } from "react";

import googleLogo from "assets/google-logo.svg";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { auth, googleAuthProvider } from "integrations/firebase/auth";

// Side-effect import: installs window.__emulatorSignIn, which DevSignIn below
// calls. The e2e suite loads this module through its own bridge, so without
// this line the button works under test and throws "bridge is not installed"
// for anyone running the app locally — the exact case it exists for. The
// module body sits behind a VITE_USE_FIREBASE_EMULATOR check, so cloud builds
// eliminate it.
import "integrations/firebase/emulator-test-auth";

import {
  awaitInviteVerdict,
  extractInviteToken,
  inviteTokenFromLocation,
  sendSignInLinkRequest,
  type InviteVerdict,
} from "./email-link";

/**
 * The sign-in control, separated from the screen that used to own it.
 *
 * Two surfaces need it and they are not the same shape: the full-page gate, and
 * an event microsite where a signed-out invitee has already been shown the
 * event and needs a way in. Keeping the popup call, the emulator fallback and
 * the error handling in one place is what stops the second surface shipping a
 * subtly different sign-in — or, as it did until now, none at all.
 *
 * Errors are shown verbatim. The auth blocking functions refuse domains outside
 * app/infrastructure/config/sign_in.py, and their reason arrives inside the auth
 * error message — swallowing it turns a policy decision into a broken button.
 * The one exception is a Google pop-up that could not finish, which gets a
 * message saying what to try (explainPopupFailure below).
 */
export function SignInControls({
  label,
  needsAddress = false,
  onConfirmAddress,
}: {
  label?: string;
  /** A returning link opened where the address was never remembered. */
  needsAddress?: boolean;
  /** Completes that link. SignInScreen owns the completion and its states. */
  onConfirmAddress?: (email: string) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async (signIn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await signIn();
      // No navigation: onAuthStateChanged flips useAuthUser, which re-renders.
      // On the microsite that swaps the preview for the member experience in
      // place, so the invitee stays on the event they were sent to.
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div className="w-full" aria-busy={busy}>
      <Button
        type="button"
        variant="cta"
        disabled={busy}
        onClick={() =>
          void run(() =>
            signInWithPopup(auth, googleAuthProvider).catch(
              explainPopupFailure,
            ),
          )
        }
        data-testid="sign-in-google"
        className="w-full"
      >
        <img src={googleLogo} alt="" className="h-4 w-4" />
        {label ?? "Sign in with Google"}
      </Button>
      <p className="mt-2 text-sm text-muted-foreground">
        For Holtzbrinck Group Google accounts.
      </p>

      <EmailLink
        busy={busy}
        run={run}
        needsAddress={needsAddress}
        onConfirmAddress={onConfirmAddress}
      />

      <DevSignIn busy={busy} run={run} />

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-left text-sm text-foreground"
        >
          {error}
        </p>
      )}
    </div>
  );
}

const POPUP_FAILED_MESSAGE =
  "The Google sign-in window could not finish. If your browser is in full-screen, leave full-screen and try again. If no window opens, allow pop-ups for this site. You can also sign in with an email link instead.";

/**
 * The pop-up failures a person can do something about.
 *
 * Chrome on macOS in full-screen hides the page behind the Google window, and
 * @firebase/auth 1.13.4 then rejected with a plain "Database is closing/hidden"
 * error that carries no code (TNG-242). Newer releases fix that, but a tab still
 * running an older bundle, or a browser that blocks the window, ends up here
 * too. Every other error stays verbatim: refusals from the blocking functions
 * arrive as auth/internal-error and carry the reason the person needs.
 */
const POPUP_FAILURE_CODES = new Set([
  "auth/popup-blocked",
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/web-storage-unsupported",
]);

function explainPopupFailure(cause: unknown): never {
  const code =
    typeof cause === "object" && cause !== null && "code" in cause
      ? String(cause.code)
      : "";
  const message = cause instanceof Error ? cause.message : String(cause);
  if (
    POPUP_FAILURE_CODES.has(code) ||
    message.includes("Database is closing")
  ) {
    throw new Error(POPUP_FAILED_MESSAGE, { cause });
  }
  throw cause;
}

/**
 * Local sign-in, emulators only.
 *
 * `signInWithPopup` cannot complete against the Auth emulator — the SDK loads a
 * script from apis.google.com to bootstrap the popup — so without this the
 * gate is unusable in local development and the first instinct is to disable
 * the gate instead. It reuses the bridge the e2e suite already installs
 * (emulator-test-auth.ts), which is itself compiled out of cloud builds, and it
 * renders only when VITE_USE_FIREBASE_EMULATOR is true. Two independent reasons
 * it cannot reach production.
 *
 * The email must satisfy the blocking functions' allow-list, or the emulator's
 * own before_user_created refuses it — which is the gate working.
 */
function DevSignIn({
  busy,
  run,
}: {
  busy: boolean;
  run: (signIn: () => Promise<unknown>) => Promise<void>;
}) {
  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR !== "true") return null;

  const email =
    import.meta.env.VITE_DEV_SIGN_IN_EMAIL ?? "developer@holtzbrinck.com";

  return (
    <Button
      type="button"
      variant="tertiary"
      disabled={busy}
      onClick={() =>
        void run(() => {
          const bridge = window.__emulatorSignIn;
          if (!bridge) {
            throw new Error(
              "The emulator sign-in bridge is not installed. Is the app running with VITE_USE_FIREBASE_EMULATOR=true?",
            );
          }
          return bridge(email, "Local Developer");
        })
      }
      data-testid="sign-in-emulator"
      className="mt-3 w-full"
    >
      <TerminalSquare className="mr-2 h-4 w-4" />
      Sign in as {email} (emulator)
    </Button>
  );
}

/**
 * Passwordless sign-in for people outside the Holtzbrinck Workspace tenant.
 *
 * Google's consent screen is Internal audience, so it refuses partner-
 * organisation attendees at the identity provider. This admits them without
 * depending on what their employer runs, and without trusting an assertion
 * about their address from someone else's tenant — a link sent to the address
 * proves control of it.
 *
 * The invite field (ADR-0003 as settled by D21) is what admits a stranger's
 * FIRST sign-in: a token minted by an event admin or a super admin. People on
 * an allowed domain and returning users never need it, so it sits behind a
 * control that is always on the form. Always, not "when the address looks
 * foreign": asking for an account before showing the field would leak account
 * existence. That is also why the backend computes the verdict from the token
 * alone. A token carried by the page's own link opens the field, filled in.
 *
 * The sent confirmation is the same for every address, by design, so a person
 * whose address is neither allowed nor invited gets no mail and no refusal.
 * The line under the confirmation tells them what to do without saying which
 * case they are in.
 */
function EmailLink({
  busy,
  run,
  needsAddress,
  onConfirmAddress,
}: {
  busy: boolean;
  run: (signIn: () => Promise<unknown>) => Promise<void>;
  needsAddress: boolean;
  onConfirmAddress?: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [initialInvite] = useState(
    () => extractInviteToken(inviteTokenFromLocation()) ?? "",
  );
  const [invite, setInvite] = useState(initialInvite);
  const [inviteOpen, setInviteOpen] = useState(initialInvite !== "");
  const [sent, setSent] = useState(false);
  const [verdict, setVerdict] = useState<InviteVerdict | null>(null);

  if (needsAddress) {
    return (
      <div className="mt-3 space-y-2 text-left">
        <p className="text-sm text-muted-foreground">
          Confirm the address this link was sent to.
        </p>
        <Label htmlFor="email-link-confirm" className="sr-only">
          Email address
        </Label>
        <Input
          id="email-link-confirm"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          testId="email-link-confirm-input"
        />
        <Button
          type="button"
          variant="secondary"
          disabled={busy || !email.trim() || !onConfirmAddress}
          onClick={() => onConfirmAddress?.(email)}
          data-testid="email-link-confirm"
          className="w-full"
        >
          Finish signing in
        </Button>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="text-left">
        <OrDivider />
        <div className="space-y-3">
          {verdict === "rejected" && (
            <p
              role="status"
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-foreground"
              data-testid="invite-rejected"
            >
              That invite has expired, been withdrawn, or is full. If you
              already have an account your link is on its way anyway — otherwise
              ask for a fresh invite.
            </p>
          )}
          <p
            className="text-sm text-muted-foreground"
            data-testid="email-link-sent"
          >
            Check your inbox: we sent a sign-in link to {email.trim()}. It opens
            this page and signs you in.
          </p>
          <p className="text-sm text-muted-foreground">
            Nothing after a few minutes? Your address may not be invited yet.
            Ask your event organiser for an invite.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-left">
      <OrDivider />
      <p className="mb-3 text-sm text-foreground">
        <strong className="font-semibold">Use your work email.</strong> We send
        you a link that signs you in.
      </p>
      <div className="space-y-1">
        <Label htmlFor="email-link-address" className="sr-only">
          Email address
        </Label>
        <Input
          id="email-link-address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          testId="email-link-input"
        />
        <Button
          type="button"
          variant="link"
          aria-expanded={inviteOpen}
          aria-controls="email-link-invite-panel"
          onClick={() => setInviteOpen((open) => !open)}
          data-testid="invite-toggle"
          className="h-auto min-h-11 justify-start whitespace-normal px-0 text-left max-sm:h-auto"
        >
          {inviteOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          I was invited from outside the group and have a code
        </Button>
        {inviteOpen && (
          <div id="email-link-invite-panel" className="space-y-2 pb-2">
            <Label htmlFor="email-link-invite" className="sr-only">
              Invite code
            </Label>
            <Input
              id="email-link-invite"
              value={invite}
              onChange={(e) => setInvite(e.target.value)}
              placeholder="Invite code or link"
              aria-describedby="email-link-invite-help"
              testId="invite-input"
            />
            <p
              id="email-link-invite-help"
              className="text-sm text-muted-foreground"
            >
              Paste the code or the link from your invitation. You only need it
              for your first sign-in.
            </p>
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={busy || !email.trim()}
          onClick={() =>
            void run(async () => {
              // A code left in a field that was closed again is not offered.
              const offered = inviteOpen ? invite : "";
              const token = extractInviteToken(offered);
              const requestId = await sendSignInLinkRequest(email, offered);
              setSent(true);
              if (token) {
                // The verdict is about the token, so it is only polled when a
                // token went along. Everything else stays silent — see the
                // rules comment on the verdict collection for why that is the
                // honest answer.
                void awaitInviteVerdict(requestId).then((next) => {
                  if (next) setVerdict(next);
                });
              }
            })
          }
          data-testid="email-link-send"
          className="w-full"
        >
          <Mail className="mr-2 h-4 w-4" />
          Email me a sign-in link
        </Button>
      </div>
    </div>
  );
}

function OrDivider() {
  return (
    <div
      className="my-5 flex items-center gap-3 text-sm text-muted-foreground"
      aria-hidden="true"
    >
      <span className="h-px flex-1 bg-border" />
      or
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
